import { lazy, Suspense, useEffect } from 'react'
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/hooks/useAuth'
import { AppLayout } from '@/components/common/layout/AppLayout'
import { Skeleton } from '@/components/ui/skeleton'

// Captured at module load, before supabase-js consumes and strips the
// auth hash. Supabase drops users at the Site URL root with
// #access_token=...&type=recovery|invite|signup — without this the app
// ignores the type and invited/recovering users never get to set a password.
const initialAuthHash = typeof window !== 'undefined' ? window.location.hash : ''

function AuthRedirectHandler() {
  const navigate = useNavigate()

  useEffect(() => {
    if (/type=(invite|signup|recovery)/.test(initialAuthHash)) {
      navigate('/reset-password', { replace: true })
    }
    const { data: sub } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY') {
        navigate('/reset-password', { replace: true })
      }
    })
    return () => sub.subscription.unsubscribe()
  }, [navigate])

  return null
}

// Auth
import { LoginPage } from '@/features/auth/pages/LoginPage'
import { ResetPasswordPage } from '@/features/auth/pages/ResetPasswordPage'

// Lazy load all feature pages
const DashboardPage        = lazy(() => import('@/features/dashboard/pages/DashboardPage').then(m => ({ default: m.DashboardPage })))
const CustomerListPage     = lazy(() => import('@/features/customers/pages/CustomerListPage').then(m => ({ default: m.CustomerListPage })))
const CustomerDetailPage   = lazy(() => import('@/features/customers/pages/CustomerDetailPage').then(m => ({ default: m.CustomerDetailPage })))
const CustomerFormPage     = lazy(() => import('@/features/customers/pages/CustomerFormPage').then(m => ({ default: m.CustomerFormPage })))
const ProductListPage      = lazy(() => import('@/features/products/pages/ProductListPage').then(m => ({ default: m.ProductListPage })))
const InvoiceListPage      = lazy(() => import('@/features/invoices/pages/InvoiceListPage').then(m => ({ default: m.InvoiceListPage })))
const InvoiceFormPage      = lazy(() => import('@/features/invoices/pages/InvoiceFormPage').then(m => ({ default: m.InvoiceFormPage })))
const InvoiceDetailPage    = lazy(() => import('@/features/invoices/pages/InvoiceDetailPage').then(m => ({ default: m.InvoiceDetailPage })))
const PaymentListPage      = lazy(() => import('@/features/payments/pages/PaymentListPage').then(m => ({ default: m.PaymentListPage })))
const ExpensePaymentsPage  = lazy(() => import('@/features/payments/pages/ExpensePaymentsPage').then(m => ({ default: m.ExpensePaymentsPage })))
const ExpenseListPage      = lazy(() => import('@/features/expenses/pages/ExpenseListPage').then(m => ({ default: m.ExpenseListPage })))
const ReferralsPage        = lazy(() => import('@/features/referrals/pages/ReferralsPage').then(m => ({ default: m.ReferralsPage })))
const EmployeeListPage     = lazy(() => import('@/features/hr/pages/EmployeeListPage').then(m => ({ default: m.EmployeeListPage })))
const PayrollPage          = lazy(() => import('@/features/hr/pages/PayrollPage').then(m => ({ default: m.PayrollPage })))
const LeavePage            = lazy(() => import('@/features/hr/pages/LeavePage').then(m => ({ default: m.LeavePage })))
const VendorListPage       = lazy(() => import('@/features/vendors/pages/VendorListPage').then(m => ({ default: m.VendorListPage })))
const ReportsPage          = lazy(() => import('@/features/reports/pages/ReportsPage').then(m => ({ default: m.ReportsPage })))
const LedgersPage          = lazy(() => import('@/features/ledgers/pages/LedgersPage').then(m => ({ default: m.LedgersPage })))
const AiPage               = lazy(() => import('@/features/ai/pages/AiPage').then(m => ({ default: m.AiPage })))
const SettingsPage         = lazy(() => import('@/features/settings/pages/SettingsPage').then(m => ({ default: m.SettingsPage })))
const NotificationsPage    = lazy(() => import('@/features/notifications/pages/NotificationsPage').then(m => ({ default: m.NotificationsPage })))
const AuditLogPage         = lazy(() => import('@/features/audit/pages/AuditLogPage').then(m => ({ default: m.AuditLogPage })))
const OnboardingPage       = lazy(() => import('@/features/onboarding/pages/OnboardingPage').then(m => ({ default: m.OnboardingPage })))
const ProfilePage          = lazy(() => import('@/features/profile/pages/ProfilePage').then(m => ({ default: m.ProfilePage })))

function PageLoader() {
  return (
    <div className="p-8 space-y-4">
      <Skeleton className="h-8 w-48" />
      <Skeleton className="h-4 w-96" />
      <div className="grid grid-cols-4 gap-4 mt-6">
        {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-32" />)}
      </div>
    </div>
  )
}

function ProtectedRoutes() {
  const { user, loading } = useAuth()
  const location = useLocation()

  const { data: hasOrg, isLoading: orgLoading } = useQuery({
    queryKey: ['has-organization', user?.id],
    queryFn: async () => {
      const { count } = await supabase
        .from('organization_members')
        .select('id', { count: 'exact', head: true })
        .eq('user_id', user!.id)
      return (count ?? 0) > 0
    },
    enabled: !!user,
    staleTime: Infinity,
  })

  if (loading || (user && orgLoading)) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    )
  }

  if (!user) return <Navigate to="/login" replace />

  // First-time users must complete onboarding
  if (hasOrg === false && location.pathname !== '/onboarding') {
    return <Navigate to="/onboarding" replace />
  }

  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route element={<AppLayout />}>
          <Route index element={<Navigate to="/dashboard" replace />} />
          <Route path="dashboard"      element={<DashboardPage />} />
          {/* Customers */}
          <Route path="customers"       element={<CustomerListPage />} />
          <Route path="customers/new"   element={<CustomerFormPage />} />
          <Route path="customers/:id"   element={<CustomerDetailPage />} />
          <Route path="customers/:id/edit" element={<CustomerFormPage />} />
          {/* Products */}
          <Route path="products"        element={<ProductListPage />} />
          {/* Invoices */}
          <Route path="invoices"        element={<InvoiceListPage />} />
          <Route path="invoices/new"    element={<InvoiceFormPage />} />
          <Route path="invoices/:id"    element={<InvoiceDetailPage />} />
          <Route path="invoices/:id/edit" element={<InvoiceFormPage />} />
          {/* Payments */}
          <Route path="payments"        element={<PaymentListPage />} />
          {/* Expenses */}
          <Route path="expenses"        element={<ExpenseListPage />} />
          <Route path="expense-payments" element={<ExpensePaymentsPage />} />
          {/* Referrals */}
          <Route path="referrals"       element={<ReferralsPage />} />
          {/* HR */}
          <Route path="employees"       element={<EmployeeListPage />} />
          <Route path="payroll"         element={<PayrollPage />} />
          <Route path="leaves"          element={<LeavePage />} />
          {/* Vendors */}
          <Route path="vendors"         element={<VendorListPage />} />
          {/* Ledgers */}
          <Route path="ledgers"         element={<LedgersPage />} />
          {/* Reports */}
          <Route path="reports/*"       element={<ReportsPage />} />
          {/* AI */}
          <Route path="ai"              element={<AiPage />} />
          {/* Settings */}
          <Route path="settings/*"      element={<SettingsPage />} />
          {/* Other */}
          <Route path="notifications"   element={<NotificationsPage />} />
          <Route path="profile"         element={<ProfilePage />} />
          <Route path="audit-logs"      element={<AuditLogPage />} />
          <Route path="documents"       element={<div className="p-8"><h1 className="text-2xl font-semibold">Documents</h1><p className="text-muted-foreground mt-2">Coming soon.</p></div>} />
        </Route>
        <Route path="onboarding" element={<OnboardingPage />} />
      </Routes>
    </Suspense>
  )
}

export function AppRouter() {
  return (
    <>
      <AuthRedirectHandler />
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/*" element={<ProtectedRoutes />} />
      </Routes>
    </>
  )
}
